/**
 * useHotkeys - Global keyboard shortcut hook
 *
 * Supports mod+k (cmd on Mac, ctrl on Windows) and escape.
 * Optionally disables on form elements.
 */

import { useEffect, useCallback } from 'react'

type HotkeyCallback = (event: KeyboardEvent) => void

interface HotkeyOptions {
  enableOnFormTags?: boolean // If false, ignores hotkey when focused on input/textarea/select
  enabled?: boolean          // If false, disables the hotkey entirely
}

/**
 * Parse hotkey string like 'mod+k' or 'escape'
 * mod = cmd on Mac, ctrl on Windows
 */
function parseHotkey(keys: string) {
  const parts = keys.toLowerCase().split('+')
  const hasModifier = parts.includes('mod')
  const key = parts[parts.length - 1]

  return {
    hasModifier,
    key,
  }
}

/**
 * Check if the event matches the hotkey
 */
function matchesHotkey(event: KeyboardEvent, keys: string): boolean {
  const { hasModifier, key } = parseHotkey(keys)
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0

  // Check modifier key (cmd on Mac, ctrl on Windows)
  const modifierPressed = isMac ? event.metaKey : event.ctrlKey
  const wrongModifierPressed = isMac ? event.ctrlKey : event.metaKey

  // If hotkey requires modifier
  if (hasModifier) {
    if (!modifierPressed || wrongModifierPressed) return false
  } else {
    // No modifier should be pressed
    if (event.metaKey || event.ctrlKey || event.altKey) return false
  }

  // Check key
  return event.key.toLowerCase() === key
}

/**
 * Check if the active element is a form field
 */
function isFormElement(element: Element | null): boolean {
  if (!element) return false

  const tagName = element.tagName
  return (
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    tagName === 'SELECT' ||
    element.hasAttribute('contenteditable')
  )
}

/**
 * useHotkeys hook
 *
 * @param keys - Hotkey string like 'mod+k', 'mod+p', 'escape'
 * @param callback - Function to call when hotkey is pressed
 * @param options - Configuration options
 *
 * @example
 * useHotkeys('mod+k', () => setOpen(true))
 * useHotkeys('escape', () => setOpen(false), { enableOnFormTags: false })
 */
export function useHotkeys(
  keys: string,
  callback: HotkeyCallback,
  options: HotkeyOptions = {}
) {
  const { enableOnFormTags = false, enabled = true } = options

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Check if hotkey is enabled
      if (!enabled) return

      // Check if we're on a form element and should ignore
      if (!enableOnFormTags && isFormElement(document.activeElement)) {
        return
      }

      // Check if the event matches our hotkey
      if (matchesHotkey(event, keys)) {
        event.preventDefault()
        callback(event)
      }
    },
    [keys, callback, enabled, enableOnFormTags]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
}
